import "./styles.css";
import { useEditOnboarding } from "../../hooks/useEditOnboarding";
import { Card } from "../Card/Index";
import ChipPressableComponent from "../Chips/ComponentChipPressable/Index";
import Pagination from "../Pagination";
import { useFilter } from "../../hooks/useFilter";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { AnimalCategory } from "../SystemManager/Animals/WeightCategories/AnimalCategory/Index";
import { Loading } from "../Loading/Index";
import { DateModal } from "../Modal/DateFilter";
import { formatDateWithoutHour, parseUtcDate } from "../../utils/date/formatedDate";
import { IOptionsProps } from "../../@types/reactSelect/IOption";
import { DropdownV2, formatPhone, SearchInputV2 } from "../FormControlsV2";
import { WeightAnimals } from "../SystemManager/Animals/WeightCategories/WeightAnimals";
import { Salter, salterService } from "../../services/salterService";
import { useTranslation } from "react-i18next";

function SalterTags({ items }: { items: { id: number; name: string }[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const hoveredRef = useRef(false);

  useEffect(() => {
    let animationFrame = 0;
    let direction = 1;
    let position = 0;
    let previousTime = 0;

    function animate(currentTime: number) {
      const container = containerRef.current;
      const isMobile = window.matchMedia("(max-width: 768px)").matches;
      const elapsedSeconds = previousTime ? Math.min((currentTime - previousTime) / 1000, 0.1) : 0;
      previousTime = currentTime;
      if (container && (isMobile || hoveredRef.current)) {
        const maxScroll = container.scrollWidth - container.clientWidth;
        if (maxScroll > 0) {
          position += 24 * elapsedSeconds * direction;
          if (position >= maxScroll) {
            position = maxScroll;
            direction = -1;
          }
          if (position <= 0) {
            position = 0;
            direction = 1;
          }
          container.scrollLeft = position;
        }
      }
      animationFrame = window.requestAnimationFrame(animate);
    }

    animationFrame = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(animationFrame);
  }, []);

  return (
    <div
      className="salters__tags"
      ref={containerRef}
      onMouseEnter={() => { hoveredRef.current = true; }}
      onMouseLeave={() => { hoveredRef.current = false; }}
    >
      {items.map((item) => (
        <span className="salters__tag" key={item.id}>{item.name}</span>
      ))}
    </div>
  );
}

function SaltersCardsView() {
  const { t } = useTranslation();
  const { setOptionForm, setDefaultValue, setIdSelected } = useEditOnboarding();
  const [rows, setRows] = useState<Salter[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    salterService.list(page, query)
      .then((result) => {
        setRows(result.data);
        setTotalPages(result.total_pages);
      })
      .finally(() => setLoading(false));
  }, [page, query]);

  return (
    <>
      <div className="filter__content">
        <SearchInputV2
          className="salters-search"
          name="salters-search"
          type={t("systemManager.searchSalters")}
          onSearch={(value) => {
            setPage(1);
            setQuery(value || "");
          }}
        />
      </div>
      {!!rows.length ? (
        <section className="grid__infos">
          <Loading visible={loading} />
          {rows.map((salter) => (
            <button
              key={`${salter.id || "user"}-${salter.user.id}`}
              className="salters__card"
              onClick={() => {
                setDefaultValue(salter);
                setIdSelected(salter.id || 0);
                setOptionForm("salters");
              }}
            >
              <strong>{salter.user.name}</strong>
              <span>{salter.user.phone ? formatPhone(salter.user.phone) : t("systemManager.phoneUnavailable")}</span>
              {!!salter.farms?.length && (
                <div className="salters__card-field">
                  <b>{t("systemManager.farmCount", { count: salter.farms.length })}</b>
                  <SalterTags items={salter.farms} />
                </div>
              )}
              {!!salter.areas?.length && (
                <div className="salters__card-field">
                  <b>{t("systemManager.areaCount", { count: salter.areas.length })}</b>
                  <SalterTags items={salter.areas} />
                </div>
              )}
            </button>
          ))}
        </section>
      ) : (
        <div className="emptyTable_container">
          <Loading visible={loading} />
          {!loading && <span>{t("systemManager.noResults")}</span>}
        </div>
      )}
      {!!rows.length && (
        <>
          <div className="delimiter viewCards__pagination-delimiter" />
          <div className="paginator">
            <Pagination totalPages={totalPages} currentPage={page} onClick={setPage} />
          </div>
        </>
      )}
    </>
  );
}

export function ViewCards({ mobileHeaderAction }: { mobileHeaderAction?: ReactNode }) {
  const { t } = useTranslation();
  const {
    filterSelected,
    setFilterSelected,
    listCards,
    setOptionForm,
    totalPages,
    currentPage,
    setIdSelected,
    farmIdSelected,
    setFarmIdSelected,
    setCardInfo,
    setDefaultValue,
    setCurrentPage,
    setQueryToSearch,
    isLoading
  } = useEditOnboarding();

  const {
    sideBarOptionSelected,
    rangeDays,
    setRangeDays,
    setModalInventoryTaskStop,
    systemManager,
    responseFarms,
  } = useFilter();
  const [customComponent, setCustomComponent] = useState<ReactNode | undefined>(undefined);
  const optionsWithoutSearch = ["animal-category", "pasture-management", "batch-inventory", "weight-animals"];
  const optionsWithFarmSelect = ["batch-inventory"];
  const customColorCard = ["batch-inventory"];

  useEffect(() => {
    if (filterSelected == "batch-inventory") {
      const now = new Date();
      const lastYear = new Date(now.getFullYear() - 1, now.getMonth(), now.getDay());

      setRangeDays({
        from: lastYear,
        to: now
      })
    }
  }, []);

  const farmOptions: IOptionsProps = useMemo(() => {
    if (!responseFarms?.length) return [];
    return [...responseFarms]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((farm) => ({
        label: farm.name,
        value: farm.id,
        info: farm,
      }));
  }, [responseFarms]);

  const selectedFarmOption = useMemo(
    () =>
      farmOptions.find((option) => Number(option.value) === Number(farmIdSelected)) ||
      farmOptions[0] ||
      null,
    [farmOptions, farmIdSelected],
  );

  useEffect(() => {
    switch (filterSelected) {
      case "animal-category":
        setCustomComponent(() => <AnimalCategory farmSelected={selectedFarmOption} />);
        break;
      case "weight-animals":
        setCustomComponent(() => <WeightAnimals farmSelected={selectedFarmOption} />);
        break;
      case "pasture-management":
        break;
      default:
        setCustomComponent(() => undefined);
        break;
    }
  }, [filterSelected, selectedFarmOption]);

  const OptionsMap = new Map();
  OptionsMap.set("farms", t("systemManager.farm"));
  OptionsMap.set("areas", t("systemManager.area"));
  OptionsMap.set("micro-areas", t("systemManager.paddock"));
  OptionsMap.set("batches", t("systemManager.batch"));


  const showFarmName = ["pasture-management"]
  const showAreaName = ["pasture-management"]

  const inventoryColorsMap = new Map()
  inventoryColorsMap.set("DRAFT", "#ff9501af")
  inventoryColorsMap.set("PENDING", "#0157ffaf")
  inventoryColorsMap.set("COMPLETED", "#62b8aeaf")

  if (systemManager === "SALTERS") return <SaltersCardsView />;

  return (
    <>
      {/* Filtro */}
      <div className="filter__content">
        {systemManager == "PROPERTIES_AREAS" && (
          <>
            <ChipPressableComponent
              theme={filterSelected == "farms" ? "menu-dark" : "menu-light"}
              label={t("systemManager.farms")}
              onClick={() => setFilterSelected("farms")}
            />
            <ChipPressableComponent
              theme={filterSelected == "areas" ? "menu-dark" : "menu-light"}
              label={t("systemManager.areas")}
              onClick={() => setFilterSelected("areas")}
            />
            <ChipPressableComponent
              theme={
                filterSelected == "micro-areas" ? "menu-dark" : "menu-light"
              }
              label={t("systemManager.paddocks")}
              onClick={() => setFilterSelected("micro-areas")}
            />
          </>
        )}

        {systemManager == "CATTLE_MAP" && (
          <>
            <ChipPressableComponent
              theme={filterSelected == "animal-category" ? "menu-dark" : "menu-light"}
              label={t("systemManager.animalCategory")}
              onClick={() => setFilterSelected("animal-category")}
            />
            <ChipPressableComponent
              theme={filterSelected == "weight-animals" ? "menu-dark" : "menu-light"}
              label={t("systemManager.weights")}
              onClick={() => setFilterSelected("weight-animals")}
            />
          </>
        )}

        {filterSelected && !optionsWithoutSearch.includes(filterSelected) &&
          <>
            <SearchInputV2
              name="view-cards-search"
              type={OptionsMap.get(filterSelected)}
              onSearch={(query) => setQueryToSearch(query)}
            />
          </>
        }

        {filterSelected && optionsWithFarmSelect.includes(filterSelected) &&
          <div className="filters-dropdown" style={{ width: 400 }}>
            <DropdownV2
              name="view-cards-farm"
              options={farmOptions}
              value={selectedFarmOption}
              placeholder={t("systemManager.selectFarm")}
              menuMount="overlay"
              onChange={(option) => {
                if (option) setFarmIdSelected(Number(option.value));
              }}
            />
          </div>
        }
      </div>
      {/* Filtro */}

      {!!customComponent ?
        <div className={`children__content${mobileHeaderAction ? " children__content--mobile-action" : ""}`}>
          {mobileHeaderAction && <div className="system-manager__mobile-content-action">{mobileHeaderAction}</div>}
          {customComponent}
        </div>
        :
        <>
          {/* Visualização dos cards */}
          {listCards.length > 0 ?
            <section className="grid__infos">
              <Loading visible={isLoading} />

              {filterSelected && listCards.map((card) => {
                function getCardBackgroundColor(card: any) {
                  if (card.state === "PENDING") return "#0157ffaf";
                  if (card.state === "DRAFT") return "#ff9501af";
                  if (filterSelected && card.date && rangeDays?.from) {
                    const cardDate = parseUtcDate(card.date)!;
                    const fromDate = new Date(rangeDays.from);
                    return cardDate > fromDate
                      ? inventoryColorsMap.get(card.state)
                      : "#ff6161af";
                  }
                  return "#ff6161af";
                }

                function disabledEdit(card: any) {
                  return filterSelected !== "batch-inventory" || getCardBackgroundColor(card) !== "#ff6161af";
                }

                return (
                  <Card
                    type={filterSelected}
                    initials={card.initial}
                    name={card.name}
                    qtdArea={card.qtdArea}
                    qtdMicroArea={card.qtdMicroArea}
                    qtdAnimals={card.qtdAnimals}
                    initialBatch={card.initialBatch}
                    onClick={() => {
                      setIdSelected(card.id);
                      if (filterSelected == "areas" || filterSelected == "pasture-management" || filterSelected == "batch-inventory") setOptionForm(filterSelected)
                      if (filterSelected === "batch-inventory") {
                        const isDraft = !disabledEdit(card);
                        setModalInventoryTaskStop(isDraft);
                        const formatedCard = isDraft
                          ? { ...card, state: undefined, id: null, date: undefined }
                          : card;
                        setIdSelected(formatedCard.id);
                        setCardInfo(formatedCard);
                      }
                    }}
                    managementType={card.managementType}
                    showFarm={showFarmName.includes(filterSelected)}
                    showArea={showAreaName.includes(filterSelected)}
                    nameFarm={card.info && card.info.micro_area! ? card.info.micro_area[0].farm.name : null}
                    nameArea={card.info && card.info.micro_area! ? card.info.micro_area[0].area.name : null}
                    listInitialsMicroAreas={card.info && card.info.micro_area! && card.info.micro_area.map((mc: any) => mc.initials)}
                    date={card.date ? formatDateWithoutHour(parseUtcDate(card.date)!) : undefined}
                    style={
                      customColorCard.includes(filterSelected) ? {
                        backgroundColor: getCardBackgroundColor(card),
                      } : undefined
                    }
                    description={card.description}
                    state={!disabledEdit(card) ? "DRAFT" : card.state}
                  />
                );
              })}
            </section>
            : <div className="emptyTable_container">
              <span>{t("commonUi.noResults")}</span>
            </div>
          }
          {/* Visualização dos cards */}

          {/* Paginação */}
          {
            listCards.length ?
              <>
                <div className="delimiter viewCards__pagination-delimiter" />
                <div className="paginator">

                  {filterSelected == "batch-inventory" &&
                    <div className={"gridSubtitleContainer"}>
                      <div className={"gridSubtitle"} style={{ background: "#ff6161af" }}>
                        Não realizado
                      </div>
                      <div className={"gridSubtitle"} style={{ background: "#ff9501af" }}>
                        Em preenchimento
                      </div>
                      <div className={"gridSubtitle"} style={{ background: "#0157ffaf" }} >
                        Enviado para capataz
                      </div>
                      <div className={"gridSubtitle"} style={{ background: "#62b8aeaf" }}>
                        Concluído
                      </div>
                    </div>
                  }

                  <Pagination
                    totalPages={totalPages}
                    currentPage={currentPage}
                    onClick={(value: number) => setCurrentPage(value)}
                  />
                </div>
              </>
              : <></>
          }
          {/* Paginação */}
        </>
      }

      {/* Modals*/}
      <DateModal />
      {/* Modals */}
    </>
  );
}
